import AutoCompleteParticipantModal from '@/components/modals/autocomplete-participant-modal/AutoCompleteParticipantModal';
import CalendarModal from '@/components/modals/calendar-modal/CalendarModal';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import useMixPanel from '@/hooks/useMixPanel';
import { TUserData } from '@/services/api/user-api';
import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import IdentificationTypesSelect from '../../input/identification-types-select/IdentificationTypesSelect';

type ParticipantCampusProps = {
  form: UseFormReturn<any, any, undefined>;
  handleBlur: () => void;
  user: TUserData | undefined;
};

const ParticipantCampus: React.FC<ParticipantCampusProps> = ({
  form,
  handleBlur,
  user
}) => {
  const Mixpanel = useMixPanel();
  const { t } = useTranslation(['newBooking', 'common']);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<string>('');

  const participantFormValue = form.getValues().student;
  const participantAvailable = user?.consumers?.filter(
    (consumer) => !participantFormValue?.id && !consumer?.isOwnUser
  );
  const isSelectedExistingParticipant = Boolean(participantFormValue?.id);
  const disabledParticipantInputs = isSelectedExistingParticipant;

  const handleSelectAutoCompleteParticipant = (id: string) => {
    Mixpanel.track('Action: Select participant from autocomplete', {
      screen: 'New Booking',
      organization: 'Windsurf Area - Campus nautico',
      participantId: id
    });

    const participant = user?.consumers?.find((consumer) => consumer.id === id);

    // Get the last parent
    const parent =
      participant?.additionalData?.parents?.[
        participant.additionalData.parents.length - 1
      ];

    if (participant) {
      const participantFormValue = {
        id: participant.id,
        firstName: participant.firstName,
        lastName: participant.lastName,
        identification: {
          number: participant.identification?.number,
          type: participant.identification?.type
        },
        birthdate: participant.birthdate
          ? new Date(participant.birthdate)
          : undefined,
        illness: {
          value: participant.additionalData?.illness?.value,
          answer: participant.additionalData?.illness?.answer
        }
      };

      if (parent) {
        const parentFormValue = {
          firstName: parent.firstName,
          lastName: parent.lastName,
          phone: parent.phone,
          identification: {
            number: parent.identification?.number,
            type: parent.identification?.type
          },
          email: parent.email,
          address: parent.address,
          location: parent.location,
          postalCode: parent.postalCode
        };

        form.setValue('parent', parentFormValue, {
          shouldValidate: true
        });
      }

      // First we update the participant field
      form.setValue('student', participantFormValue, {
        shouldValidate: true
      });

      // after setting the participant, we set the illness
      form.setValue('student.illness', participantFormValue.illness, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });
    }
    setIsOpenDialog(false);
  };
  const removeSelectedParticipant = async () => {
    Mixpanel.track('Action: Remove selected participant', {
      screen: 'New Booking',
      organization: 'Windsurf Area - Campus nautico'
    });

    form.reset({
      ...form.getValues(),
      parent: {
        firstName: '',
        lastName: '',
        phone: '',
        identification: {
          number: '',
          type: ''
        },
        email: '',
        address: '',
        location: '',
        postalCode: ''
      },
      student: {
        id: '',
        firstName: '',
        lastName: '',
        identification: {
          number: '',
          type: ''
        },
        birthdate: undefined,
        illness: {
          value: false,
          answer: ''
        }
      }
    });
    form.clearErrors();
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Datos del padre/madre/tutor (1) */}
      <div className="border border-primary p-6 rounded-md space-y-4">
        <h3 className="font-bold">{t('newBooking:titleParent')}</h3>

        {/* Nombre */}
        <FormField
          control={form.control}
          name="user.firstName"
          render={({ field }) => {
            const disabled = !!user?.firstName;
            return (
              <FormItem>
                <FormLabel>
                  {t('newBooking:stepParticipants.form.firstName')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={disabled}
                    onClick={() =>
                      Mixpanel.track('press user first name input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Campus nautico'
                      })
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* Apellido */}
        <FormField
          control={form.control}
          name="user.lastName"
          render={({ field }) => {
            const disabled = !!user?.lastName;
            return (
              <FormItem>
                <FormLabel>
                  {t('newBooking:stepParticipants.form.lastName')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={disabled}
                    onClick={() =>
                      Mixpanel.track('press user last name input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Campus nautico'
                      })
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* Teléfono */}
        <FormField
          control={form.control}
          name="user.phone"
          render={({ field }) => {
            const disabled = !!user?.phone;
            return (
              <FormItem>
                <FormLabel>{t('newBooking:phone')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={disabled}
                    onClick={() =>
                      Mixpanel.track('press user phone input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Campus nautico'
                      })
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="user.identification.type"
          render={({ field }) => {
            const disabled = !!user?.identification?.type;
            return (
              <FormItem>
                <IdentificationTypesSelect
                  field={field}
                  defaultValue={field?.value}
                  disabled={disabled}
                  onValueChange={(value) => {
                    Mixpanel.track('press user id input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico',
                      value
                    });
                    field.onChange(value);
                  }}
                />
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="user.identification.number"
          render={({ field }) => {
            const disabled = !!user?.identification?.number;
            return (
              <FormItem>
                <FormLabel>
                  {t('stepParticipants.form.identificationNumber')}
                </FormLabel>
                <FormControl>
                  <Input
                    onClick={() =>
                      Mixpanel.track('press participants id number input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Campus nautico'
                      })
                    }
                    placeholder={t('stepParticipants.form.id')}
                    {...field}
                    value={field?.value || ''}
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* Email */}
        <FormField
          control={form.control}
          name="user.email"
          render={({ field }) => {
            const disabled = !!user?.email;
            return (
              <FormItem>
                <FormLabel>
                  {t('newBooking:stepParticipants.form.mail')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onBlur={handleBlur}
                    disabled={disabled}
                    onClick={() =>
                      Mixpanel.track('press user email input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Campus nautico'
                      })
                    }
                  />
                </FormControl>
                <FormDescription>
                  {t('newBooking:stepParticipants.form.mail_description')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* Domicilio */}
        <FormField
          control={form.control}
          name="user.address"
          render={({ field }) => {
            const disabled = !!user?.address;
            return (
              <FormItem>
                <FormLabel>{t('newBooking:address')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={disabled}
                    onClick={() =>
                      Mixpanel.track('press user address input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Campus nautico'
                      })
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* Localidad */}
        <FormField
          control={form.control}
          name="user.location"
          render={({ field }) => {
            const disabled = !!user?.location;
            return (
              <FormItem>
                <FormLabel>{t('newBooking:location')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Madrid"
                    disabled={disabled}
                    onClick={() =>
                      Mixpanel.track('press user location input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Campus nautico'
                      })
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* CP */}
        <FormField
          control={form.control}
          name="user.postalCode"
          render={({ field }) => {
            const disabled = !!user?.postalCode;
            return (
              <FormItem>
                <FormLabel>{t('newBooking:postalCode')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={disabled}
                    onClick={() =>
                      Mixpanel.track('press user postal code input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Campus nautico'
                      })
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </div>

      {/* Datos del padre/madre/tutor (2) */}
      <div className="border border-primary p-6 rounded-md space-y-4">
        <h3 className="font-bold">
          {t('newBooking:titleParent')} ({t('newBooking:optional')})
        </h3>

        {/* Nombre */}
        <FormField
          control={form.control}
          name="parent.firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('newBooking:stepParticipants.form.firstName')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Introduce el nombre"
                  disabled={disabledParticipantInputs}
                  onClick={() =>
                    Mixpanel.track('press parent first name input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico'
                    })
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Apellido */}
        <FormField
          control={form.control}
          name="parent.lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('newBooking:stepParticipants.form.lastName')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Introduce el segundo apellido"
                  disabled={disabledParticipantInputs}
                  onClick={() =>
                    Mixpanel.track('press parent last name input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico'
                    })
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Teléfono */}
        <FormField
          control={form.control}
          name="parent.phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('newBooking:phone')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Introduce el teléfono"
                  disabled={disabledParticipantInputs}
                  onClick={() =>
                    Mixpanel.track('press parent phone input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico'
                    })
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="parent.identification.type"
          render={({ field }) => {
            return (
              <FormItem>
                <IdentificationTypesSelect
                  field={field}
                  defaultValue={field?.value}
                  disabled={disabledParticipantInputs}
                  onValueChange={(value) => {
                    Mixpanel.track('press parent id input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico',
                      value
                    });
                    field.onChange(value);
                  }}
                />
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="parent.identification.number"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>
                  {t('stepParticipants.form.identificationNumber')}
                </FormLabel>
                <FormControl>
                  <Input
                    onClick={() =>
                      Mixpanel.track('press parent id number input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Campus nautico'
                      })
                    }
                    placeholder={t('stepParticipants.form.id')}
                    {...field}
                    value={field?.value || ''}
                    disabled={disabledParticipantInputs}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* Email */}
        <FormField
          control={form.control}
          name="parent.email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('newBooking:stepParticipants.form.mail')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Introduce el correo electrónico"
                  disabled={disabledParticipantInputs}
                  onClick={() =>
                    Mixpanel.track('press parent email input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico'
                    })
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Domicilio */}
        <FormField
          control={form.control}
          name="parent.address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('newBooking:address')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={disabledParticipantInputs}
                  onClick={() =>
                    Mixpanel.track('press parent address input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico'
                    })
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Localidad */}
        <FormField
          control={form.control}
          name="parent.location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('newBooking:location')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Madrid"
                  disabled={disabledParticipantInputs}
                  onClick={() =>
                    Mixpanel.track('press parent location input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico'
                    })
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* CP */}
        <FormField
          control={form.control}
          name="parent.postalCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('newBooking:postalCode')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Introduce su CP"
                  disabled={disabledParticipantInputs}
                  onClick={() =>
                    Mixpanel.track('press parent postal code input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico'
                    })
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Datos del alumno */}
      <div className="border border-primary p-6 rounded-md space-y-4">
        <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
          <h3 className="font-bold">{t('newBooking:titleStudent')}</h3>
          <AutoCompleteParticipantModal
            open={isOpenDialog}
            onOpenChange={setIsOpenDialog}
            consumers={participantAvailable}
            selectedParticipant={selectedParticipant}
            setSelectedParticipant={setSelectedParticipant}
            handleSelect={handleSelectAutoCompleteParticipant}
            removeSelectedParticipant={removeSelectedParticipant}
            formValue={participantFormValue}
          />
        </div>
        {/* Nombre */}
        <FormField
          control={form.control}
          name="student.firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('newBooking:stepParticipants.form.firstName')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Introduce el nombre del alumno"
                  disabled={disabledParticipantInputs}
                  onClick={() =>
                    Mixpanel.track('press student first name input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico'
                    })
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Primer Apellido */}
        <FormField
          control={form.control}
          name="student.lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('newBooking:stepParticipants.form.lastName')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Introduce el apellido del alumno"
                  disabled={disabledParticipantInputs}
                  onClick={() =>
                    Mixpanel.track('press student last name input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico'
                    })
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="student.identification.type"
          render={({ field }) => {
            return (
              <FormItem>
                <IdentificationTypesSelect
                  field={field}
                  defaultValue={field?.value}
                  disabled={disabledParticipantInputs}
                  onValueChange={(value) => {
                    Mixpanel.track('press student id input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico',
                      value
                    });
                    field.onChange(value);
                  }}
                />
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="student.identification.number"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>
                  {t('stepParticipants.form.identificationNumber')}
                </FormLabel>
                <FormControl>
                  <Input
                    onClick={() =>
                      Mixpanel.track('press student id number input', {
                        screen: 'New Booking',
                        organization: 'Windsurf Area - Campus nautico'
                      })
                    }
                    placeholder={t('stepParticipants.form.id')}
                    {...field}
                    value={field?.value || ''}
                    disabled={disabledParticipantInputs}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {/* Fecha de Nacimiento */}
        <FormField
          control={form.control}
          name="student.birthdate"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Fecha de Nacimiento</FormLabel>
              <CalendarModal
                value={field.value}
                placeholder={t('stepParticipants.form.birth')}
                disabledInputLabel={disabledParticipantInputs}
                onAccept={(value) => {
                  Mixpanel.track('press student birthdate input', {
                    screen: 'New Booking',
                    organization: 'Windsurf Area - Campus nautico',
                    value
                  });
                  field.onChange(value);
                }}
                initialFocus
                dropDownPicker
              />
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Enfermedad */}
        <FormField
          control={form.control}
          name="student.illness"
          render={({ field }) => {
            const defaultValue =
              field?.value?.value === undefined
                ? undefined
                : field?.value?.value
                ? 'yes'
                : 'no';
            return (
              <FormItem>
                <div className="flex flex-row items-end gap-5">
                  <FormLabel>{t('newBooking:illness')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={(value) => {
                        Mixpanel.track('press student illness input', {
                          screen: 'New Booking',
                          organization: 'Windsurf Area - Campus nautico',
                          value
                        });
                        field.onChange({
                          ...field.value,
                          value: value === 'yes'
                        });
                      }}
                      defaultValue={defaultValue}
                      className="flex space-x-2 mt-0 items-center"
                      disabled={disabledParticipantInputs}
                    >
                      <FormItem className="flex items-center space-x-1 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="yes" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          {t('yes')}
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-1 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="no" />
                        </FormControl>
                        <FormLabel className="font-normal">{t('no')}</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                </div>

                {field?.value?.value && (
                  <div className="px-2 py-1">
                    <Textarea
                      placeholder={field?.value?.placeholder || t('extraInfo')}
                      {...field}
                      disabled={disabledParticipantInputs}
                      onChange={(e) =>
                        field.onChange({
                          ...field.value,
                          answer: e.target.value
                        })
                      }
                      onClick={() =>
                        Mixpanel.track('press student illness answer input', {
                          screen: 'New Booking',
                          organization: 'Windsurf Area - Campus nautico'
                        })
                      }
                      value={field?.value?.answer || ''}
                    />
                    {/* {answerError && (
                        <p className="text-sm font-medium text-destructive">
                          {answerError.message}
                        </p>
                      )} */}
                  </div>
                )}
              </FormItem>
            );
          }}
        />
      </div>

      <div>
        <p className="text-xs font-medium">
          {t('newBooking:stepParticipants.form.responsibility')}{' '}
          <a
            // TODO: Remove hardcoded url
            href="https://windsurfarea.com/escuela-nautica-reglamento/"
            target="blank"
            className="text-primary underline"
            rel="noreferrer noopener"
          >
            {t('newBooking:stepParticipants.form.responsibilityLink')}
          </a>
        </p>
      </div>

      {/* Privacidad de datos  */}
      <FormField
        control={form.control}
        name="acceptDataPrivacy"
        render={({ field }) => (
          <FormItem>
            <div className="flex flex-start">
              <FormControl>
                <Checkbox
                  id="isAcceptDataPrivacy"
                  checked={field.value}
                  onCheckedChange={() => {
                    Mixpanel.track('press privacy policy input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico'
                    });
                    field.onChange(!field.value);
                  }}
                />
              </FormControl>
              <FormLabel htmlFor="isAcceptDataPrivacy" className="ml-2">
                {t('newBooking:stepParticipants.form.privacyPolicy')}{' '}
                <a
                  // TODO: Remove hardcoded url
                  href="https://windsurfarea.com/politica-privacidad/"
                  target="blank"
                  className="text-primary underline"
                  rel="noreferrer noopener"
                >
                  {t('newBooking:stepParticipants.form.privacyPolicyLink')}
                </a>
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Fotografía  */}
      <FormField
        control={form.control}
        name="acceptTakePhotos"
        render={({ field }) => (
          <FormItem>
            <div className="flex flex-start">
              <FormControl>
                <Checkbox
                  id="isAcceptTakePhotos"
                  checked={field.value}
                  onCheckedChange={() => {
                    Mixpanel.track('press take photos input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico'
                    });
                    field.onChange(!field.value);
                  }}
                />
              </FormControl>
              <FormLabel htmlFor="isAcceptTakePhotos" className="ml-2">
                {t('newBooking:stepParticipants.form.takePhotos')}
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Ofrecimiento de productos/servicios  */}
      <FormField
        control={form.control}
        name="authOfferProducts"
        render={({ field }) => (
          <FormItem>
            <div className="flex flex-start">
              <FormControl>
                <Checkbox
                  id="isAuthOfferProducts"
                  checked={field.value}
                  onCheckedChange={() => {
                    Mixpanel.track('press offer products input', {
                      screen: 'New Booking',
                      organization: 'Windsurf Area - Campus nautico'
                    });
                    field.onChange(!field.value);
                  }}
                />
              </FormControl>
              <FormLabel htmlFor="isAuthOfferProducts" className="ml-2">
                {t('newBooking:stepParticipants.form.acceptOffers')}
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default ParticipantCampus;
